import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Divider,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomButton from "../../../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { BASE_URL } from "../../../../components/BaseUrl";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import { toast } from "react-hot-toast";

const SelectPeriodModal = ({ open, onClose, onConfirm }) => {
  const [period, setPeriod] = useState("");
  const [periods, setPeriods] = useState([]);
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const {t} = useTranslation();
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;

  // Fetch available periods when modal opens
  useEffect(() => {
    if (open && decryptedToken) {
      fetchAvailablePeriods();
    } else if (!open) {
      // Reset when modal closes
      setPeriod("");
      setPeriods([]);
    }
  }, [open, decryptedToken]);

  const fetchAvailablePeriods = async () => {
    setLoadingPeriods(true);
    try {
      const res = await fetch(`${BASE_URL}/documents/worker/payslips/periods`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${decryptedToken}`,
        },
      });

      const data = await res.json();
      console.log("data period", data);
      if (!data.error && data.data?.periods) {
        // Show ALL periods - don't filter duplicates
        // Use paid_at date to distinguish between periods with same value
        setPeriods(data.data.periods);
        
        console.log("All periods:", data.data.periods);
        
        // Always set default to first available period when modal opens
        // Use composite value (period|paid_at) to make it unique
        if (data.data.periods.length > 0) {
          const firstPeriod = data.data.periods[0];
          const firstPeriodValue = firstPeriod.paid_at 
            ? `${firstPeriod.period}|${firstPeriod.paid_at}` 
            : `${firstPeriod.period}|0`;
          console.log("Setting default period to:", firstPeriodValue);
          setPeriod(firstPeriodValue);
        } else {
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, "0");
          setPeriod(`${year}-${month}`);
        }
      } else {
        toast.error(data.message || "Failed to fetch available periods.");
        // Fallback to current month
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        setPeriod(`${year}-${month}`);
      }
    } catch (err) {
      console.error("Error fetching periods:", err);
      toast.error("Failed to load available periods.");
      // Fallback to current month
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      setPeriod(`${year}-${month}`);
    } finally {
      setLoadingPeriods(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) {
        return dateString;
      }
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  const handleConfirm = () => {
    if (!period) {
      toast.error("Please select a period");
      return;
    }
    
    // Extract period from composite value (format: "period|paid_at" or "period|index")
    let actualPeriod = period;
    if (period.includes('|')) {
      actualPeriod = period.split('|')[0];
    }
    
    console.log("✅ Confirming period selection:", actualPeriod);
    console.log("✅ Full period value:", period);
    console.log("✅ Selected period data:", selectedPeriodData);
    
    // Pass the actual period value (not the composite) to parent
    onConfirm(actualPeriod);
    // Close modal - parent will handle preview
    onClose();
  };

  // Get selected period data - handle composite value
  const getSelectedPeriodData = () => {
    if (!period) return null;
    
    // Extract period and paid_at from composite value
    let periodValue = period;
    let paidAtValue = null;
    
    if (period.includes('|')) {
      const parts = period.split('|');
      periodValue = parts[0];
      paidAtValue = parts[1];
    }
    
    // Find the period using composite matching
    if (paidAtValue) {
      return periods.find((p) => 
        String(p.period) === String(periodValue) && 
        String(p.paid_at) === String(paidAtValue)
      );
    }
    
    // Fallback: find by period only
    return periods.find((p) => String(p.period) === String(periodValue));
  };
  
  const selectedPeriodData = getSelectedPeriodData();

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 380,
          bgcolor: "#fff",
          borderRadius: 3,
          boxShadow: 24,
          p: 3,
          fontFamily: "Poppins, sans-serif",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: "#003149",
              fontFamily: "Poppins, sans-serif",
            }}
          >
  {t("selectPeriodModal.title")}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {/* Period Selector */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: 13,
              color: "#44546F",
              mb: 1,
              fontWeight: 500,
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {t("selectPeriodModal.label") || "Select Period"}
          </Typography>
          {loadingPeriods ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <FormControl fullWidth>
              {/* <InputLabel id="period-select-label">{t("selectPeriodModal.label") || "Select Period"}</InputLabel> */}
              <Select
                labelId="period-select-label"
                value={period || ""}
                // label={t("selectPeriodModal.label") || "Select Period"}
                onChange={(e) => {
                  const newValue = e.target.value;
                  console.log("🔄 Period changed to:", newValue);
                  console.log("🔄 Current period state before update:", period);
                  
                  // Store the composite value
                  setPeriod(newValue);
                  
                  // Extract and log the selected period data
                  let periodValue = newValue;
                  let paidAtValue = null;
                  if (newValue.includes('|')) {
                    const parts = newValue.split('|');
                    periodValue = parts[0];
                    paidAtValue = parts[1];
                  }
                  
                  const selectedData = paidAtValue 
                    ? periods.find((p) => String(p.period) === String(periodValue) && String(p.paid_at) === String(paidAtValue))
                    : periods.find((p) => String(p.period) === String(periodValue));
                  
                  console.log("🔄 Selected period data:", selectedData);
                }}
                renderValue={(selected) => {
                  if (!selected || periods.length === 0) return "";
                  
                  // Extract period and paid_at from composite value
                  let periodValue = selected;
                  let paidAtValue = null;
                  
                  if (selected.includes('|')) {
                    const parts = selected.split('|');
                    periodValue = parts[0];
                    paidAtValue = parts[1];
                  }
                  
                  // Find the selected period using the composite value
                  let selectedPeriod = null;
                  if (paidAtValue) {
                    selectedPeriod = periods.find((p) => 
                      String(p.period) === String(periodValue) && 
                      String(p.paid_at) === String(paidAtValue)
                    );
                  } else {
                    // Fallback: find by period only
                    selectedPeriod = periods.find((p) => String(p.period) === String(periodValue));
                  }
                  
                  if (selectedPeriod) {
                    // Show paid_at date prominently to distinguish duplicates
                    if (selectedPeriod.paid_at) {
                      return `Paid: ${formatDate(selectedPeriod.paid_at)}`;
                    }
                    return selectedPeriod.period_label || selectedPeriod.period || selected;
                  }
                  return selected;
                }}
                sx={{
                  fontSize: 14,
                  fontFamily: "Poppins, sans-serif",
                  height: "42px",
                }}
              >
                {periods.map((p, index) => {
                  // Create unique key using index and paid_at to handle duplicates
                  const uniqueKey = `period-${index}-${p.paid_at || index}`;
                  // Use a composite value: period + paid_at to make each option unique
                  const uniqueValue = p.paid_at ? `${p.period}|${p.paid_at}` : `${p.period}|${index}`;
                  return (
                    <MenuItem 
                      key={uniqueKey} 
                      value={uniqueValue}
                      selected={period === uniqueValue}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                    {p.period_label || p.period}
                        </Typography>
                        {p.paid_at && (
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: "#666",
                              mt: 0.5,
                            }}
                          >
                            Paid: {formatDate(p.paid_at)}
                          </Typography>
                        )}
                      </Box>
                  </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}
          
          {/* Show paid at date for selected period */}
          {selectedPeriodData?.paid_at && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                bgcolor: "#f5f6f7",
                borderRadius: 1,
                borderLeft: "3px solid #006EC2",
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#44546F",
                  fontWeight: 500,
                  mb: 0.5,
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                {t("paid_date")}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: "#172B4D",
                  fontWeight: 600,
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                {formatDate(selectedPeriodData.paid_at)}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CustomButton
  label={t("selectPeriodModal.buttons.confirm")}
            size="small"
            onClick={handleConfirm}
            disabled={!period}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default SelectPeriodModal;
